import { z } from 'zod';

import {
  CurrencyCodeSchema,
  IsoDateTimeSchema,
  PaiseSchema,
  UuidSchema,
  BooleanQueryParamSchema,
} from './common.js';
import { GenderSchema } from './customers.js';
import { PaginationQuerySchema, paginatedResponseSchema } from './pagination.js';

/**
 * Booking shapes (Phase 6 - bookings + payments).
 *
 * Layout mirrors leads/customers:
 *   - BookingListItem  - light row for grids + dashboards
 *   - BookingDetail    - the booking-edit page (passengers + purchases + payments joined)
 *   - Create/Update    - sales-agent write paths (DRAFT bookings only)
 *   - Confirm / Cancel - explicit verb DTOs that freeze totals + flip status
 *   - Passenger / Purchase verb DTOs - add / update / remove individual rows
 *
 * Totals are frozen at CONFIRMED; pre-confirmation they sync from the quote.
 * Money is paise on the wire (JS-safe int) - see PaiseSchema in common.ts.
 */

// ---------------------------------------------------------------------------
// Enums (mirror prisma/schema.prisma)
// ---------------------------------------------------------------------------

export const BookingStatusSchema = z.enum([
  'DRAFT',
  'CONFIRMED',
  'IN_PROGRESS',
  'COMPLETED',
  'CANCELLED',
]);
export type BookingStatus = z.infer<typeof BookingStatusSchema>;

export const BookingPurchaseStatusSchema = z.enum([
  'PENDING',
  'CONFIRMED',
  'CANCELLED',
  'REFUNDED',
]);
export type BookingPurchaseStatus = z.infer<typeof BookingPurchaseStatusSchema>;

export const BookingPurchaseTypeSchema = z.enum([
  'FLIGHT',
  'HOTEL',
  'ACTIVITY',
  'TRANSFER',
  'VISA',
  'INSURANCE',
  'OTHER',
]);
export type BookingPurchaseType = z.infer<typeof BookingPurchaseTypeSchema>;

export const PassengerTypeSchema = z.enum(['ADULT', 'CHILD', 'INFANT']);
export type PassengerType = z.infer<typeof PassengerTypeSchema>;

// ---------------------------------------------------------------------------
// Reference sub-schemas
// ---------------------------------------------------------------------------

const NamedRefSchema = z.object({ id: UuidSchema, name: z.string() });
const UserRefSchema = z.object({ id: UuidSchema, fullName: z.string() });
const CustomerRefSchema = z.object({
  id: UuidSchema,
  displayName: z.string(),
  email: z.string().nullable(),
  mobileNumber: z.string().nullable(),
});
const LeadRefSchema = z.object({
  id: UuidSchema,
  reference: z.string(),
  title: z.string(),
});
const QuoteRefSchema = z.object({
  id: UuidSchema,
  quoteNumber: z.string(),
  grandTotalPaise: PaiseSchema,
});

// ---------------------------------------------------------------------------
// Passengers
// ---------------------------------------------------------------------------

export const BookingPassengerSchema = z.object({
  id: UuidSchema,
  passengerType: PassengerTypeSchema,
  title: z.string().nullable(),
  firstName: z.string(),
  lastName: z.string(),
  dateOfBirth: z.iso.date().nullable(),
  gender: GenderSchema.nullable(),
  nationality: z.string().nullable(),
  passportNumber: z.string().nullable(),
  passportExpiry: z.iso.date().nullable(),
  passportIssuingCountry: z.string().nullable(),
  panNumber: z.string().nullable(),
  isLeadPax: z.boolean(),
  createdAt: IsoDateTimeSchema,
  updatedAt: IsoDateTimeSchema,
});
export type BookingPassenger = z.infer<typeof BookingPassengerSchema>;

export const BookingPassengerInputSchema = z.object({
  passengerType: PassengerTypeSchema,
  title: z.string().max(10).nullable().optional(),
  firstName: z.string().min(1).max(120),
  lastName: z.string().min(1).max(120),
  dateOfBirth: z.iso.date().nullable().optional(),
  gender: GenderSchema.nullable().optional(),
  /** ISO-3166-1 alpha-2 (e.g. "IN") - server upper-cases + validates length 2. */
  nationality: z.string().length(2).nullable().optional(),
  passportNumber: z.string().max(20).nullable().optional(),
  passportExpiry: z.iso.date().nullable().optional(),
  passportIssuingCountry: z.string().length(2).nullable().optional(),
  /** Indian PAN, format AAAAA9999A. Server validates regex. */
  panNumber: z
    .string()
    .regex(/^[A-Z]{5}[0-9]{4}[A-Z]$/, 'Invalid PAN format')
    .nullable()
    .optional(),
  isLeadPax: z.boolean().default(false),
});
export type BookingPassengerInput = z.infer<typeof BookingPassengerInputSchema>;

// ---------------------------------------------------------------------------
// Purchases (supplier POs)
// ---------------------------------------------------------------------------

export const BookingPurchaseSchema = z.object({
  id: UuidSchema,
  supplier: NamedRefSchema,
  purchaseType: BookingPurchaseTypeSchema,
  status: BookingPurchaseStatusSchema,
  confirmationReference: z.string().nullable(),
  serviceDate: z.iso.date().nullable(),
  serviceDescription: z.string().nullable(),
  costPaise: PaiseSchema,
  currency: z.object({ id: UuidSchema, code: CurrencyCodeSchema }).nullable(),
  vendorInvoiceS3Key: z.string().nullable(),
  cancellationDeadline: IsoDateTimeSchema.nullable(),
  notes: z.string().nullable(),
  createdAt: IsoDateTimeSchema,
  updatedAt: IsoDateTimeSchema,
});
export type BookingPurchase = z.infer<typeof BookingPurchaseSchema>;

export const BookingPurchaseInputSchema = z.object({
  supplierId: UuidSchema,
  purchaseType: BookingPurchaseTypeSchema,
  confirmationReference: z.string().max(120).nullable().optional(),
  serviceDate: z.iso.date().nullable().optional(),
  serviceDescription: z.string().max(500).nullable().optional(),
  costPaise: PaiseSchema,
  currencyId: UuidSchema.nullable().optional(),
  cancellationDeadline: IsoDateTimeSchema.nullable().optional(),
  notes: z.string().max(2000).nullable().optional(),
});
export type BookingPurchaseInput = z.infer<typeof BookingPurchaseInputSchema>;

export const UpdateBookingPurchaseStatusRequestSchema = z.object({
  status: BookingPurchaseStatusSchema,
  /** Only required when transitioning to CONFIRMED. */
  confirmationReference: z.string().max(120).optional(),
  /** Only used when transitioning to REFUNDED - amount actually clawed back. */
  refundPaise: PaiseSchema.optional(),
  /** S3 key of the uploaded vendor invoice (PDF/email screenshot). */
  vendorInvoiceS3Key: z.string().max(512).optional(),
});
export type UpdateBookingPurchaseStatusRequest = z.infer<
  typeof UpdateBookingPurchaseStatusRequestSchema
>;

// ---------------------------------------------------------------------------
// Booking list + detail
// ---------------------------------------------------------------------------

export const BookingListItemSchema = z.object({
  id: UuidSchema,
  bookingNumber: z.string(),
  status: BookingStatusSchema,
  customer: CustomerRefSchema,
  lead: LeadRefSchema,
  salesAgent: UserRefSchema.nullable(),
  grandTotalPaise: PaiseSchema,
  amountPaidPaise: PaiseSchema,
  amountDuePaise: PaiseSchema,
  currency: z.object({ id: UuidSchema, code: CurrencyCodeSchema }).nullable(),
  travelStartDate: z.iso.date().nullable(),
  travelEndDate: z.iso.date().nullable(),
  paxCount: z.number().int(),
  confirmedAt: IsoDateTimeSchema.nullable(),
  createdAt: IsoDateTimeSchema,
});
export type BookingListItem = z.infer<typeof BookingListItemSchema>;

export const BookingListQuerySchema = PaginationQuerySchema.extend({
  q: z.string().trim().min(1).max(120).optional(),
  customerId: UuidSchema.optional(),
  leadId: UuidSchema.optional(),
  salesAgentId: UuidSchema.optional(),
  status: BookingStatusSchema.optional(),
  /** Bookings whose travel window overlaps this date range. */
  travelDateFrom: z.iso.date().optional(),
  travelDateTo: z.iso.date().optional(),
  /** Bookings created in the last N days. Server clamps to [1, 365]. */
  createdInLastDays: z.coerce.number().int().min(1).max(365).optional(),
  includeDeleted: BooleanQueryParamSchema.optional(),
});
export type BookingListQuery = z.infer<typeof BookingListQuerySchema>;

export const BookingListResponseSchema = paginatedResponseSchema(BookingListItemSchema);
export type BookingListResponse = z.infer<typeof BookingListResponseSchema>;

export const BookingDetailSchema = z.object({
  id: UuidSchema,
  bookingNumber: z.string(),
  status: BookingStatusSchema,
  lead: LeadRefSchema,
  customer: CustomerRefSchema,
  quote: QuoteRefSchema.nullable(),
  itinerary: z.object({ id: UuidSchema, title: z.string() }).nullable(),
  currency: z.object({ id: UuidSchema, code: CurrencyCodeSchema }).nullable(),
  salesAgent: UserRefSchema.nullable(),
  // Totals
  netCostPaise: PaiseSchema,
  markupPaise: PaiseSchema,
  discountPaise: PaiseSchema,
  gstPaise: PaiseSchema,
  tcsPaise: PaiseSchema,
  grandTotalPaise: PaiseSchema,
  amountPaidPaise: PaiseSchema,
  amountDuePaise: PaiseSchema,
  refundedPaise: PaiseSchema,
  // Travel
  travelStartDate: z.iso.date().nullable(),
  travelEndDate: z.iso.date().nullable(),
  paxCount: z.number().int(),
  // Lifecycle
  confirmedAt: IsoDateTimeSchema.nullable(),
  completedAt: IsoDateTimeSchema.nullable(),
  cancelledAt: IsoDateTimeSchema.nullable(),
  cancellationReason: z.string().nullable(),
  notes: z.string().nullable(),
  // Nested collections
  passengers: z.array(BookingPassengerSchema),
  purchases: z.array(BookingPurchaseSchema),
  createdAt: IsoDateTimeSchema,
  updatedAt: IsoDateTimeSchema,
  deletedAt: IsoDateTimeSchema.nullable(),
});
export type BookingDetail = z.infer<typeof BookingDetailSchema>;

// ---------------------------------------------------------------------------
// Create / update / lifecycle verbs
// ---------------------------------------------------------------------------

export const CreateBookingRequestSchema = z
  .object({
    leadId: UuidSchema,
    customerId: UuidSchema,
    /** Required for new bookings - the quote being materialised. Without
     *  a quote, totals must be supplied explicitly via UpdateBooking. */
    quoteId: UuidSchema.nullable().optional(),
    itineraryId: UuidSchema.nullable().optional(),
    currencyId: UuidSchema.nullable().optional(),
    salesAgentId: UuidSchema.nullable().optional(),
    travelStartDate: z.iso.date().nullable().optional(),
    travelEndDate: z.iso.date().nullable().optional(),
    paxCount: z.number().int().min(1).default(1),
    notes: z.string().max(4000).nullable().optional(),
    passengers: z.array(BookingPassengerInputSchema).optional(),
  })
  .refine(
    (v) =>
      v.travelStartDate === null ||
      v.travelStartDate === undefined ||
      v.travelEndDate === null ||
      v.travelEndDate === undefined ||
      v.travelEndDate >= v.travelStartDate,
    { message: 'travelEndDate must not precede travelStartDate', path: ['travelEndDate'] },
  );
export type CreateBookingRequest = z.infer<typeof CreateBookingRequestSchema>;

export const UpdateBookingRequestSchema = z
  .object({
    salesAgentId: UuidSchema.nullable().optional(),
    travelStartDate: z.iso.date().nullable().optional(),
    travelEndDate: z.iso.date().nullable().optional(),
    paxCount: z.number().int().min(1).optional(),
    /** Overrides quote-derived totals. Only allowed while status=DRAFT. */
    netCostPaise: PaiseSchema.optional(),
    markupPaise: PaiseSchema.optional(),
    discountPaise: PaiseSchema.optional(),
    gstPaise: PaiseSchema.optional(),
    tcsPaise: PaiseSchema.optional(),
    grandTotalPaise: PaiseSchema.optional(),
    notes: z.string().max(4000).nullable().optional(),
  })
  .refine(
    (v) =>
      v.travelStartDate === null ||
      v.travelStartDate === undefined ||
      v.travelEndDate === null ||
      v.travelEndDate === undefined ||
      v.travelEndDate >= v.travelStartDate,
    { message: 'travelEndDate must not precede travelStartDate', path: ['travelEndDate'] },
  );
export type UpdateBookingRequest = z.infer<typeof UpdateBookingRequestSchema>;

export const ConfirmBookingRequestSchema = z.object({
  /** Optional supplier purchases to seed alongside confirmation. Mirrors the
   *  common ops flow: confirm the booking and immediately attach the flight
   *  PNR, hotel confirmation, etc. */
  purchases: z.array(BookingPurchaseInputSchema).optional(),
});
export type ConfirmBookingRequest = z.infer<typeof ConfirmBookingRequestSchema>;

export const CancelBookingRequestSchema = z.object({
  reason: z.string().min(3).max(2000),
});
export type CancelBookingRequest = z.infer<typeof CancelBookingRequestSchema>;
